<template>
  <el-dialog
      :title="dialogTitle"
      :visible.sync="visible"
      width="640px"
      :close-on-click-modal="false"
      @open="onOpen"
      @close="onClose"
  >
    <PayeeMatchUsageGuide scene="dialog"/>
    <el-form label-width="120px" v-loading="loading">
      <el-form-item :label="$t('text.copyFromChannel')">
        <div style="display: flex; gap: 8px; width: 100%">
          <el-select
              v-model="fromChannelId"
              filterable
              clearable
              style="flex: 1"
              :placeholder="$t('message.choosePayinChannelToCopy')"
          >
            <el-option
                v-for="item in copySourceOptions"
                :key="item.value"
                :label="`${item.value} ${item.name}`"
                :value="item.value"
            />
          </el-select>
          <el-button
              type="warning"
              plain
              size="small"
              :disabled="!fromChannelId"
              :loading="copying"
              @click="copyFromChannel"
          >{{ $t('text.import') }}
          </el-button>
        </div>
      </el-form-item>

      <el-form-item :label="$t('text.payeeMatchAccounts')">
        <div class="tag-field">
          <el-tag
              v-for="(tag, index) in form.accounts"
              :key="`acc-${tag}-${index}`"
              closable
              size="small"
              class="tag-item"
              @close="removeTag('accounts', index)"
          >{{ tag }}
          </el-tag>
          <el-input
              v-model="accountInput"
              class="tag-input"
              size="small"
              :placeholder="$t('message.inputPayeeMatchAccounts')"
              @keyup.enter.native="addTag('accounts', 'accountInput')"
              @blur="addTag('accounts', 'accountInput')"
          />
        </div>
        <div class="hint">{{ $t('message.payeeMatchAccountsHint') }}</div>
      </el-form-item>

      <el-form-item :label="$t('text.payeeMatchNames')">
        <div class="tag-field">
          <el-tag
              v-for="(tag, index) in form.names"
              :key="`name-${tag}-${index}`"
              closable
              size="small"
              class="tag-item"
              @close="removeTag('names', index)"
          >{{ tag }}
          </el-tag>
          <el-input
              v-model="nameInput"
              class="tag-input"
              size="small"
              :placeholder="$t('message.inputPayeeMatchNames')"
              @keyup.enter.native="addTag('names', 'nameInput')"
              @blur="addTag('names', 'nameInput')"
          />
        </div>
        <div class="hint">{{ $t('message.payeeMatchNamesHint') }}</div>
      </el-form-item>

      <el-form-item :label="$t('text.payeeMatchEnabled')">
        <el-switch v-model="form.enabled"/>
      </el-form-item>
    </el-form>

    <div slot="footer" class="dialog-footer">
      <el-button @click="visible = false">{{ $t('text.cancel') }}</el-button>
      <el-button type="primary" :loading="saving" @click="save">{{ $t('text.confirm') }}</el-button>
    </div>
  </el-dialog>
</template>

<script>
import PayeeMatchUsageGuide from "./PayeeMatchUsageGuide.vue";

export default {
  name: "PayeeMatchConfigDialog",
  components: {
    PayeeMatchUsageGuide
  },
  props: {
    show: {
      type: Boolean,
      default: false
    },
    channel: {
      type: Object,
      default: null
    },
    channelOptions: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      loading: false,
      saving: false,
      copying: false,
      fromChannelId: "",
      accountInput: "",
      nameInput: "",
      form: {
        accounts: [],
        names: [],
        enabled: true
      }
    };
  },
  computed: {
    visible: {
      get() {
        return this.show;
      },
      set(val) {
        this.$emit("update:show", val);
      }
    },
    dialogTitle() {
      if (!this.channel) {
        return this.$t("text.payeeMatchConfig");
      }
      return `${this.$t("text.payeeMatchConfig")} — ${this.channel.channelId} ${this.channel.channelName || ""}`;
    },
    copySourceOptions() {
      const currentId = this.channel && this.channel.channelId;
      return (this.channelOptions || []).filter(
          item => item.value !== currentId && String(item.productId) === "110"
      );
    }
  },
  methods: {
    resetForm() {
      this.fromChannelId = "";
      this.accountInput = "";
      this.nameInput = "";
      this.form = {
        accounts: [],
        names: [],
        enabled: true
      };
    },
    applyConfig(config) {
      const data = config || {};
      this.form = {
        accounts: Array.isArray(data.accounts) ? [...data.accounts] : [],
        names: Array.isArray(data.names) ? [...data.names] : [],
        enabled: data.enabled !== false
      };
    },
    addTag(listKey, inputKey) {
      const value = (this[inputKey] || "").trim().replace(/\s+/g, " ");
      if (!value) {
        return;
      }
      const list = this.form[listKey] || [];
      const exists = list.some(item => String(item).toLowerCase() === value.toLowerCase());
      if (!exists) {
        this.form[listKey] = [...list, value];
      }
      this[inputKey] = "";
    },
    removeTag(listKey, index) {
      const list = [...(this.form[listKey] || [])];
      list.splice(index, 1);
      this.form[listKey] = list;
    },
    onOpen() {
      this.resetForm();
      if (!this.channel || !this.channel.channelId) {
        return;
      }
      this.loading = true;
      this.$axios
          .get("/routeChannelInfo/payeeMatchConfig", {
            params: {channelId: this.channel.channelId}
          })
          .then(res => {
            if (res.code === 200) {
              this.applyConfig(res.data && res.data.payeeMatchConfig);
            } else {
              this.$notify.error({
                title: this.$t("text.prompt"),
                message: res.msg
              });
            }
          })
          .finally(() => {
            this.loading = false;
          });
    },
    onClose() {
      this.resetForm();
    },
    copyFromChannel() {
      if (!this.fromChannelId || !this.channel) {
        return;
      }
      this.copying = true;
      this.$axios
          .get("/routeChannelInfo/payeeMatchConfig", {
            params: {channelId: this.fromChannelId}
          })
          .then(res => {
            if (res.code === 200) {
              this.applyConfig(res.data && res.data.payeeMatchConfig);
              this.$notify({
                title: this.$t("text.prompt"),
                message: this.$t("message.importSuccess"),
                type: "success"
              });
            } else {
              this.$notify.error({
                title: this.$t("text.prompt"),
                message: res.msg
              });
            }
          })
          .finally(() => {
            this.copying = false;
          });
    },
    save() {
      if (!this.channel || !this.channel.channelId) {
        return;
      }
      // 保存前把输入框里未回车的内容也收进去
      this.addTag("accounts", "accountInput");
      this.addTag("names", "nameInput");

      this.saving = true;
      // 全局 axios 默认 Qs.stringify 成 form；本接口需要 JSON body
      this.$axios
          .post(
              "/routeChannelInfo/payeeMatchConfigUpdate",
              {
                channelId: this.channel.channelId,
                accounts: this.form.accounts || [],
                names: this.form.names || [],
                enabled: this.form.enabled !== false
              },
              {
                headers: {"Content-Type": "application/json"},
                transformRequest: [(data) => JSON.stringify(data)]
              }
          )
          .then(res => {
            if (res.code === 200) {
              this.$notify({
                title: this.$t("text.prompt"),
                message: res.msg,
                type: "success"
              });
              this.visible = false;
              this.$emit("saved");
            } else {
              this.$notify.error({
                title: this.$t("text.prompt"),
                message: res.msg
              });
            }
          })
          .finally(() => {
            this.saving = false;
          });
    }
  }
};
</script>

<style scoped>
.tag-field {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  width: 100%;
  min-height: 32px;
  padding: 4px 8px;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  box-sizing: border-box;
}

.tag-item {
  margin: 0;
}

.tag-input {
  flex: 1;
  min-width: 160px;
}

.tag-input >>> .el-input__inner {
  border: none;
  height: 28px;
  line-height: 28px;
  padding: 0 4px;
}

.hint {
  margin-top: 4px;
  color: #909399;
  font-size: 12px;
  line-height: 1.4;
}
</style>
